/**
 * Tablet Pointer Input Controller for Paper2Ink (§6.1, Gate NT-1).
 *
 * Implements:
 * - Primary pointer ownership (mouse, pen, touch) via standard Pointer Events.
 * - Single-pointer gesture lock and palm/multi-touch rejection.
 * - Screen-to-document coordinate mapping via SVG Screen CTM inversion.
 * - Pointer-type screen-space tolerance (mouse: 4px, pen: 8px, touch: 14px)
 *   scaled to document space.
 * - Single-gesture select & immediate drag without requiring prior selection or double-tap.
 * - Preservation of existing affine transform scale and rotation during drag preview.
 * - Coalesced/latest sample consumption with RAF-throttled DOM transforms to prevent drag lag.
 * - Single-stroke rectangular marquee selection with real-time blue overlay
 *   (Windows-style drag box).
 * - Smooth pen drawing directly in document coordinates.
 * - pointercancel / lost capture cleanup without corrupting history.
 */

import type { CIMDocument, CenterlineStroke } from './model.ts';
import { hitTestPoint, hitTestRadius, selectByMarquee, type BoundingBox, type Point2D } from './selection.ts';
import { MoveObjectsCommand, AddObjectsCommand } from './commands.ts';

export type PointerType = 'mouse' | 'pen' | 'touch';

export const POINTER_TOLERANCE_PX: Record<PointerType, number> = {
  mouse: 4,
  pen: 8,
  touch: 14,
};

export const DRAG_THRESHOLD_PX = 3;
export const LASSO_MIN_POINTS = 3;
export const LASSO_MIN_BOUNDS_PX = 4;

/** Normalized rectangle from two drag corners: [minX, minY, maxX, maxY]. */
function marqueeRect(a: Point2D, b: Point2D): BoundingBox {
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[0], b[0]),
    Math.max(a[1], b[1]),
  ];
}

/**
 * Maps screen coordinates (clientX, clientY) to SVG document coordinates
 * using the SVG element's getScreenCTM() inverse, or an equivalent matrix fallback.
 */
export function screenToDocPoint(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number
): Point2D {
  try {
    const ctm = typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
    if (ctm) {
      const inv = ctm.inverse();
      if (typeof (svg as any).createSVGPoint === 'function') {
        const pt = (svg as any).createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const res = pt.matrixTransform(inv);
        return [res.x, res.y];
      }
      return [
        inv.a * clientX + inv.c * clientY + inv.e,
        inv.b * clientX + inv.d * clientY + inv.f,
      ];
    }
  } catch (_) {}

  // Fallback if CTM is unavailable
  const rect = typeof svg.getBoundingClientRect === 'function'
    ? svg.getBoundingClientRect()
    : { left: 0, top: 0, width: 800, height: 600 };
  const viewBox = svg.viewBox?.baseVal;
  const width = viewBox?.width || (svg as any).width?.baseVal?.value || 800;
  const height = viewBox?.height || (svg as any).height?.baseVal?.value || 600;
  const scaleX = rect.width > 0 ? width / rect.width : 1;
  const scaleY = rect.height > 0 ? height / rect.height : 1;
  return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY];
}

/**
 * Returns current SVG scale factor (document units per screen pixel).
 */
export function getSvgScale(svg: SVGSVGElement): number {
  try {
    const ctm = typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
    if (ctm) {
      const scale = Math.hypot(ctm.a, ctm.b);
      if (scale > 0) return scale;
    }
  } catch (_) {}

  const rect = typeof svg.getBoundingClientRect === 'function'
    ? svg.getBoundingClientRect()
    : { width: 800, height: 600 };
  const viewBox = svg.viewBox?.baseVal;
  const docW = viewBox?.width || (svg as any).width?.baseVal?.value || 800;
  return docW > 0 && rect.width > 0 ? rect.width / docW : 1;
}

/**
 * Converts screen-space selection tolerance in pixels to document units
 * based on current zoom / screen CTM.
 */
export function getDocumentTolerance(
  svg: SVGSVGElement,
  pointerType: PointerType = 'pen'
): number {
  const screenPx = POINTER_TOLERANCE_PX[pointerType] ?? 8;
  const scale = getSvgScale(svg);
  return scale > 0 ? screenPx / scale : screenPx;
}

export interface TabletPointerCallbacks {
  getDocument: () => CIMDocument;
  getTool: () => 'select' | 'lasso' | 'draw' | 'erase';
  getSelectedIds: () => Set<string>;
  onSelect: (selectedIds: Set<string>, primaryId: string | null) => void;
  onCommitMove: (cmd: MoveObjectsCommand) => void;
  onCommitDraw?: (cmd: AddObjectsCommand) => void;
  onLassoSelect: (selectedIds: string[]) => void;
  /** Commits one erase gesture as a single undoable deletion of whole objects. */
  onCommitErase?: (objectIds: string[]) => void;
  onStatus?: (msg: string) => void;
  getStrokeColor?: () => string;
  getStrokeWidth?: () => number;
}

export class TabletPointerController {
  private svg: SVGSVGElement;
  private callbacks: TabletPointerCallbacks;

  // Active primary pointer tracking
  private activePointerId: number | null = null;
  private activePointerType: PointerType = 'pen';

  // Drag gesture state
  private startScreenX = 0;
  private startScreenY = 0;
  private startDocPt: Point2D = [0, 0];
  private potentialDrag = false;
  private isDragging = false;
  private originalTransforms = new Map<string, number[]>();
  private dragElements: Map<string, SVGElement> = new Map();
  private dragPreviewGroups: SVGGElement[] = [];
  private dragGroupsPrepared = false;
  private latestDocDelta: Point2D = [0, 0];
  private rafId: number | null = null;

  // Marquee (rectangular lasso) gesture state
  private isLassoing = false;
  private lassoStartDocPt: Point2D | null = null;
  private lassoCurrentDocPt: Point2D | null = null;
  private lassoOverlayElement: SVGRectElement | null = null;

  // Draw gesture state
  private isDrawing = false;
  private drawDocPoints: Point2D[] = [];
  private drawPathData = '';
  private drawPreviewElement: SVGPathElement | null = null;

  // Erase gesture state: one gesture collects whole objects, one undo step
  private isErasing = false;
  private erasedIds = new Set<string>();

  // Event handler references for cleanup
  private onPointerDownHandler = (ev: PointerEvent) => this.handlePointerDown(ev);
  private onPointerMoveHandler = (ev: PointerEvent) => this.handlePointerMove(ev);
  private onPointerUpHandler = (ev: PointerEvent) => this.handlePointerUp(ev);
  private onPointerCancelHandler = (ev: PointerEvent) => this.handlePointerCancel(ev);

  constructor(svg: SVGSVGElement, callbacks: TabletPointerCallbacks) {
    this.svg = svg;
    this.callbacks = callbacks;
    this.attach();
  }

  public attach(): void {
    if (!this.svg) return;
    this.svg.style.touchAction = 'none';

    this.svg.addEventListener('pointerdown', this.onPointerDownHandler);
    this.svg.addEventListener('pointermove', this.onPointerMoveHandler);
    this.svg.addEventListener('pointerup', this.onPointerUpHandler);
    this.svg.addEventListener('pointercancel', this.onPointerCancelHandler);

    if (typeof window !== 'undefined') {
      window.addEventListener('pointerup', this.onPointerUpHandler);
      window.addEventListener('pointercancel', this.onPointerCancelHandler);
    }
  }

  public detach(): void {
    if (!this.svg) return;
    this.cancelActiveGesture();

    this.svg.removeEventListener('pointerdown', this.onPointerDownHandler);
    this.svg.removeEventListener('pointermove', this.onPointerMoveHandler);
    this.svg.removeEventListener('pointerup', this.onPointerUpHandler);
    this.svg.removeEventListener('pointercancel', this.onPointerCancelHandler);

    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', this.onPointerUpHandler);
      window.removeEventListener('pointercancel', this.onPointerCancelHandler);
    }
  }

  public getActivePointerId(): number | null {
    return this.activePointerId;
  }

  public getIsDragging(): boolean {
    return this.isDragging;
  }

  public getIsLassoing(): boolean {
    return this.isLassoing;
  }

  public getIsDrawing(): boolean {
    return this.isDrawing;
  }

  public cancelActiveGesture(): void {
    if (this.activePointerId !== null) {
      try {
        if (typeof this.svg.releasePointerCapture === 'function') {
          this.svg.releasePointerCapture(this.activePointerId);
        }
      } catch (_) {}
    }

    if (this.rafId !== null) {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = null;
    }

    if (this.isDragging) {
      this.releaseDragPreviewGroups();
      // Revert any DOM transform preview
      for (const [id, origT] of this.originalTransforms) {
        const elem = this.dragElements.get(id) || (this.svg.querySelector(`[data-id="${id}"]`) as SVGElement | null);
        if (elem) {
          elem.setAttribute(
            'transform',
            `matrix(${origT[0]}, ${origT[1]}, ${origT[2]}, ${origT[3]}, ${origT[4]}, ${origT[5]})`
          );
        }
      }
    }

    if (this.lassoOverlayElement) {
      if (this.lassoOverlayElement.parentNode) {
        this.lassoOverlayElement.parentNode.removeChild(this.lassoOverlayElement);
      }
      this.lassoOverlayElement = null;
    }

    if (this.drawPreviewElement) {
      if (this.drawPreviewElement.parentNode) {
        this.drawPreviewElement.parentNode.removeChild(this.drawPreviewElement);
      }
      this.drawPreviewElement = null;
    }

    this.activePointerId = null;
    this.potentialDrag = false;
    this.isDragging = false;
    this.isLassoing = false;
    this.lassoStartDocPt = null;
    this.lassoCurrentDocPt = null;
    this.isDrawing = false;
    this.isErasing = false;
    this.erasedIds.clear();
    this.originalTransforms.clear();
    this.dragElements.clear();
    this.drawDocPoints = [];
    this.drawPathData = '';
  }

  public handlePointerDown(ev: PointerEvent): void {
    const incoming = (ev.pointerType as PointerType) || 'mouse';
    // Fingers can navigate/select, but must not leave ink while a pen is in use.
    if (this.callbacks.getTool() === 'draw' && incoming === 'touch') return;

    // A touch holding a selection/lasso gesture yields to a pen. In draw
    // mode fingers are rejected before they can claim the lock at all.
    if (this.activePointerId !== null) {
      const holderIsTouch = this.activePointerType === 'touch';
      if (incoming === 'pen' && holderIsTouch) {
        this.cancelActiveGesture();
        // fall through: the pen becomes the active pointer below
      } else {
        return;
      }
    }

    this.activePointerId = ev.pointerId;
    this.activePointerType = incoming;

    try {
      if (typeof this.svg.setPointerCapture === 'function') {
        this.svg.setPointerCapture(ev.pointerId);
      }
    } catch (_) {}

    this.startScreenX = ev.clientX;
    this.startScreenY = ev.clientY;
    this.startDocPt = screenToDocPoint(this.svg, ev.clientX, ev.clientY);

    const tool = this.callbacks.getTool();
    const doc = this.callbacks.getDocument();

    if (tool === 'select') {
      const tolerance = getDocumentTolerance(this.svg, this.activePointerType);
      const targetElem = ev.target as SVGElement | null;
      const targetId = targetElem && typeof targetElem.getAttribute === 'function'
        ? targetElem.getAttribute('data-id')
        : null;
      const hitId =
        hitTestPoint(doc, this.startDocPt, tolerance) ||
        (targetId && doc.objects.some((o) => o.id === targetId) ? targetId : null);
      const currentSelected = new Set(this.callbacks.getSelectedIds());

      if (hitId) {
        if (ev.shiftKey) {
          // Shift toggle
          if (currentSelected.has(hitId)) {
            currentSelected.delete(hitId);
          } else {
            currentSelected.add(hitId);
          }
          this.callbacks.onSelect(currentSelected, hitId);
        } else {
          // Normal single press: hit-test and select immediately on pointerdown
          if (!currentSelected.has(hitId)) {
            currentSelected.clear();
            currentSelected.add(hitId);
            this.callbacks.onSelect(currentSelected, hitId);
          }
        }

        // Store original transforms for all selected objects, and resolve DOM
        // elements with a single pass over the SVG children. The old
        // per-object `querySelector` walk was 10k DOM queries on a large
        // selection; children order and data-id attributes are the source of truth.
        this.originalTransforms.clear();
        this.dragElements.clear();
        for (const obj of doc.objects) {
          if (currentSelected.has(obj.id)) {
            this.originalTransforms.set(obj.id, [...obj.transform]);
          }
        }
        if (this.originalTransforms.size > 0) {
          for (const child of Array.from(this.svg.children ?? [])) {
            const id = typeof (child as Element).getAttribute === 'function'
              ? (child as Element).getAttribute('data-id')
              : null;
            if (id && this.originalTransforms.has(id) && !this.dragElements.has(id)) {
              this.dragElements.set(id, child as unknown as SVGElement);
            }
            if (this.dragElements.size === this.originalTransforms.size) break;
          }
          // Fallback for elements outside the direct children list (rare).
          if (this.dragElements.size < this.originalTransforms.size && typeof this.svg.querySelector === 'function') {
            for (const id of this.originalTransforms.keys()) {
              if (this.dragElements.has(id)) continue;
              const elem = this.svg.querySelector(`[data-id="${id}"]`);
              if (elem) this.dragElements.set(id, elem as SVGElement);
            }
          }
        }

        this.potentialDrag = currentSelected.size > 0;
        this.isDragging = false;
        this.latestDocDelta = [0, 0];
      } else {
        // Press empty canvas: clear selection in select mode (if not shift)
        if (!ev.shiftKey) {
          if (currentSelected.size > 0) {
            currentSelected.clear();
            this.callbacks.onSelect(currentSelected, null);
          }
        }
        this.potentialDrag = false;
        this.isDragging = false;
        this.originalTransforms.clear();
        this.dragElements.clear();
      }
    } else if (tool === 'lasso') {
      // Windows-style marquee: a translucent blue rectangle follows the drag.
      this.isLassoing = true;
      this.lassoStartDocPt = this.startDocPt;
      this.lassoCurrentDocPt = this.startDocPt;

      if (typeof document !== 'undefined' && typeof document.createElementNS === 'function') {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('class', 'p2i-marquee-overlay');
        rect.setAttribute('x', String(this.startDocPt[0]));
        rect.setAttribute('y', String(this.startDocPt[1]));
        rect.setAttribute('width', '0');
        rect.setAttribute('height', '0');
        rect.setAttribute('fill', 'rgba(0, 120, 215, 0.22)');
        rect.setAttribute('stroke', '#0078d7');
        rect.setAttribute('stroke-width', '1.5');
        rect.setAttribute('pointer-events', 'none');
        this.svg.appendChild(rect);
        this.lassoOverlayElement = rect;
      }
    } else if (tool === 'erase') {
      // The eraser deletes whole vector objects. Painting white on the mask
      // canvas used to only affect the mask (and the strokes reappeared when
      // switching back to the pen); a real erase must change the document.
      this.isErasing = true;
      this.erasedIds.clear();
      const tolerance = getDocumentTolerance(this.svg, this.activePointerType);
      for (const id of hitTestRadius(this.callbacks.getDocument(), this.startDocPt, tolerance)) {
        this.erasedIds.add(id);
      }
      this.callbacks.onStatus?.(`Siliniyor: ${this.erasedIds.size} nesne`);
    } else if (tool === 'draw') {
      this.isDrawing = true;
      this.drawDocPoints = [this.startDocPt];
      this.drawPathData = `M ${this.startDocPt[0]} ${this.startDocPt[1]}`;

      if (typeof document !== 'undefined' && typeof document.createElementNS === 'function') {
        const color = this.callbacks.getStrokeColor ? this.callbacks.getStrokeColor() : '#163d58';
        const width = this.callbacks.getStrokeWidth ? this.callbacks.getStrokeWidth() : 4;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('class', 'p2i-draw-preview');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', color);
        path.setAttribute('stroke-width', String(width));
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.setAttribute('pointer-events', 'none');
        path.setAttribute('d', `M ${this.startDocPt[0]} ${this.startDocPt[1]}`);
        this.svg.appendChild(path);
        this.drawPreviewElement = path;
      }
    }
  }

  public handlePointerMove(ev: PointerEvent): void {
    if (this.activePointerId === null || ev.pointerId !== this.activePointerId) {
      return;
    }

    // Always consume the latest coalesced coordinate sample without stale queue lag
    const docPt = screenToDocPoint(this.svg, ev.clientX, ev.clientY);

    const tool = this.callbacks.getTool();

    if (tool === 'select' && this.potentialDrag) {
      const screenDx = ev.clientX - this.startScreenX;
      const screenDy = ev.clientY - this.startScreenY;
      const dist = Math.hypot(screenDx, screenDy);

      if (!this.isDragging && dist >= DRAG_THRESHOLD_PX) {
        this.isDragging = true;
      }

      if (this.isDragging) {
        const deltaX = docPt[0] - this.startDocPt[0];
        const deltaY = docPt[1] - this.startDocPt[1];
        this.latestDocDelta = [deltaX, deltaY];

        // RAF-throttled transform preview preserving original affine matrix
        this.scheduleDragPreview();
      }
    } else if (tool === 'lasso' && this.isLassoing && this.lassoStartDocPt) {
      this.lassoCurrentDocPt = docPt;
      if (this.lassoOverlayElement) {
        const [x, y, w, h] = marqueeRect(this.lassoStartDocPt, docPt);
        this.lassoOverlayElement.setAttribute('x', String(x));
        this.lassoOverlayElement.setAttribute('y', String(y));
        this.lassoOverlayElement.setAttribute('width', String(w - x));
        this.lassoOverlayElement.setAttribute('height', String(h - y));
      }
    } else if (tool === 'erase' && this.isErasing) {
      const events: Array<{ clientX: number; clientY: number }> =
        typeof (ev as any).getCoalescedEvents === 'function'
          ? (ev as any).getCoalescedEvents()
          : [ev];
      const samples = events.length > 0 ? events : [ev];
      const tolerance = getDocumentTolerance(this.svg, this.activePointerType);
      const doc = this.callbacks.getDocument();
      for (const s of samples) {
        const pt = screenToDocPoint(this.svg, s.clientX, s.clientY);
        for (const id of hitTestRadius(doc, pt, tolerance)) {
          this.erasedIds.add(id);
        }
      }
    } else if (tool === 'draw' && this.isDrawing) {
      const events: Array<{ clientX: number; clientY: number }> =
        typeof (ev as any).getCoalescedEvents === 'function'
          ? (ev as any).getCoalescedEvents()
          : [ev];
      const samples = events.length > 0 ? events : [ev];

      let added = false;
      for (const s of samples) {
        const pt = screenToDocPoint(this.svg, s.clientX, s.clientY);
        const lastPt = this.drawDocPoints[this.drawDocPoints.length - 1];
        const dist = Math.hypot(pt[0] - lastPt[0], pt[1] - lastPt[1]);

        // Relaxed threshold: coalesced high-frequency samples are preserved
        if (dist >= 0.5) {
          this.drawDocPoints.push(pt);
          this.drawPathData += ` L ${pt[0]} ${pt[1]}`;
          added = true;
        }
      }

      if (added && this.drawPreviewElement) {
        this.drawPreviewElement.setAttribute('d', this.drawPathData);
      }
    }
  }

  public handlePointerUp(ev: PointerEvent): void {
    if (this.activePointerId === null || ev.pointerId !== this.activePointerId) {
      return;
    }

    try {
      if (typeof this.svg.releasePointerCapture === 'function') {
        this.svg.releasePointerCapture(ev.pointerId);
      }
    } catch (_) {}

    const tool = this.callbacks.getTool();
    const docPt = screenToDocPoint(this.svg, ev.clientX, ev.clientY);

    if (tool === 'select') {
      if (this.isDragging) {
        // Cancel pending preview RAF
        if (this.rafId !== null) {
          if (typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(this.rafId);
          }
          this.rafId = null;
        }

        const finalDeltaX = docPt[0] - this.startDocPt[0];
        const finalDeltaY = docPt[1] - this.startDocPt[1];

        try {
          if (Math.hypot(finalDeltaX, finalDeltaY) > 0.05) {
            const selectedList = Array.from(this.callbacks.getSelectedIds());
            if (selectedList.length > 0) {
              const cmd = new MoveObjectsCommand(selectedList, finalDeltaX, finalDeltaY);
              this.callbacks.onCommitMove(cmd);
            }
          }
        } finally {
          this.releaseDragPreviewGroups();
        }
      }
    } else if (tool === 'erase' && this.isErasing) {
      // One gesture = one undoable deletion.
      const erased = Array.from(this.erasedIds);
      this.isErasing = false;
      this.erasedIds.clear();
      if (erased.length > 0) {
        this.callbacks.onCommitErase?.(erased);
        this.callbacks.onStatus?.(`${erased.length} nesne silindi.`);
      }
    } else if (tool === 'lasso' && this.isLassoing) {
      if (this.lassoOverlayElement) {
        if (this.lassoOverlayElement.parentNode) {
          this.lassoOverlayElement.parentNode.removeChild(this.lassoOverlayElement);
        }
        this.lassoOverlayElement = null;
      }
      const start = this.lassoStartDocPt;
      const end = this.lassoCurrentDocPt ?? docPt;
      this.lassoStartDocPt = null;
      this.lassoCurrentDocPt = null;

      if (start && end) {
        const rect = marqueeRect(start, end);
        const bboxW = rect[2] - rect[0];
        const bboxH = rect[3] - rect[1];

        if (bboxW >= LASSO_MIN_BOUNDS_PX || bboxH >= LASSO_MIN_BOUNDS_PX) {
          const doc = this.callbacks.getDocument();
          const selectedIds = selectByMarquee(doc, rect);
          this.callbacks.onLassoSelect(selectedIds);
          this.callbacks.onStatus?.(`Seçim: ${selectedIds.length} nesne`);
        } else {
          // A plain tap on the canvas with the marquee tool cancels the
          // current selection instead of leaving the stale one in place.
          this.callbacks.onLassoSelect([]);
          this.callbacks.onStatus?.('Seçim temizlendi.');
        }
      }
    } else if (tool === 'draw' && this.isDrawing) {
      if (this.drawPreviewElement) {
        if (this.drawPreviewElement.parentNode) {
          this.drawPreviewElement.parentNode.removeChild(this.drawPreviewElement);
        }
        this.drawPreviewElement = null;
      }

      if (this.drawDocPoints.length >= 1) {
        const points = [...this.drawDocPoints];
        if (points.length === 1) {
          points.push([points[0][0] + 0.5, points[0][1] + 0.5]);
        }

        const stroke: CenterlineStroke = {
          id: `stroke_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          kind: 'centerline_stroke',
          points,
          width: this.callbacks.getStrokeWidth ? this.callbacks.getStrokeWidth() : 4,
          color: this.callbacks.getStrokeColor ? this.callbacks.getStrokeColor() : '#163d58',
          opacity: 1.0,
          transform: [1, 0, 0, 1, 0, 0],
          source_kind: 'user',
        };

        const cmd = new AddObjectsCommand([stroke]);
        this.callbacks.onCommitDraw?.(cmd);
        this.callbacks.onStatus?.('Kalem çizgisi eklendi.');
      }
    }

    this.activePointerId = null;
    this.potentialDrag = false;
    this.isDragging = false;
    this.isLassoing = false;
    this.lassoStartDocPt = null;
    this.lassoCurrentDocPt = null;
    this.isDrawing = false;
    this.isErasing = false;
    this.erasedIds.clear();
    this.originalTransforms.clear();
    this.dragElements.clear();
    this.drawDocPoints = [];
    this.drawPathData = '';
  }

  public handlePointerCancel(ev: PointerEvent): void {
    if (ev.pointerId === this.activePointerId) this.cancelActiveGesture();
  }

  private prepareDragPreviewGroups(): void {
    if (this.dragGroupsPrepared) return;
    this.dragGroupsPrepared = true;
    if (this.dragElements.size < 80 || typeof document === 'undefined') return;
    // Group only adjacent selected paths. Never reorder non-selected siblings:
    // their paint order relative to selected paths is part of the document.
    const runs: SVGElement[][] = [];
    let run: SVGElement[] = [];
    for (const child of Array.from(this.svg.children)) {
      if (this.dragElements.has(child.getAttribute('data-id') || '')) {
        run.push(child as SVGElement);
      } else if (run.length) {
        runs.push(run);
        run = [];
      }
    }
    if (run.length) runs.push(run);
    if (runs.length > this.dragElements.size / 4) return;
    for (const siblings of runs) {
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      this.svg.insertBefore(group, siblings[0]);
      for (const element of siblings) group.appendChild(element);
      this.dragPreviewGroups.push(group);
    }
  }

  private releaseDragPreviewGroups(): void {
    for (const group of this.dragPreviewGroups) {
      const parent = group.parentNode;
      if (!parent) continue;
      while (group.firstChild) parent.insertBefore(group.firstChild, group);
      parent.removeChild(group);
    }
    this.dragPreviewGroups = [];
    this.dragGroupsPrepared = false;
  }

  private scheduleDragPreview(): void {
    if (this.rafId !== null) return;

    const render = () => {
      this.rafId = null;
      if (!this.isDragging) return;

      const [dx, dy] = this.latestDocDelta;
      this.prepareDragPreviewGroups();
      if (this.dragPreviewGroups.length) {
        for (const group of this.dragPreviewGroups) {
          group.setAttribute('transform', `translate(${dx} ${dy})`);
        }
        return;
      }

      // Small or interleaved selections keep the per-element preview.
      for (const [id, origT] of this.originalTransforms) {
        let elem = this.dragElements.get(id);
        if (!elem) {
          elem = (this.svg.querySelector(`[data-id="${id}"]`) as SVGElement) || undefined;
          if (elem) this.dragElements.set(id, elem);
        }
        if (elem) {
          const [a, b, c, d, e, f] = origT;
          elem.setAttribute(
            'transform',
            `matrix(${a}, ${b}, ${c}, ${d}, ${e + dx}, ${f + dy})`
          );
        }
      }
    };

    if (typeof requestAnimationFrame === 'function') {
      this.rafId = requestAnimationFrame(render);
    } else {
      render();
    }
  }
}
